// Line Execute — Confirm-payload resolver.
//
// Adapters that return NEEDS_CONFIRMATION emit a `confirmPayload`. When the
// user picks the affirmative chip on that turn, the panel calls
// resolveLineExecuteConfirmation(payload, context) to actually perform the
// mutation.
//
// Every resolver is thin and fully catches its own errors — no exception
// crosses this boundary. Resolvers return a normalized outcome the same
// shape as a Phase 1 adapter run, so the panel-side rendering code is
// reused verbatim.

import type { LineExecuteContext, LineExecuteOutcome } from './dispatcher'

// The confirm payload is opaque to the dispatcher — each adapter owns its
// own shape. We type-narrow inside resolveLineExecuteConfirmation.
export interface LineExecuteConfirmPayload {
  kind: string
  [key: string]: unknown
}

export type LineExecuteConfirmResolver = (
  payload: LineExecuteConfirmPayload,
  context: LineExecuteContext,
) => Promise<LineExecuteOutcome>

const RESOLVERS = new Map<string, LineExecuteConfirmResolver>()

export const registerLineExecuteConfirmResolver = (
  kind: string,
  resolver: LineExecuteConfirmResolver,
): void => {
  RESOLVERS.set(kind, resolver)
}

export const resolveLineExecuteConfirmation = async (
  payload: LineExecuteConfirmPayload,
  context: LineExecuteContext,
): Promise<LineExecuteOutcome> => {
  const resolver = RESOLVERS.get(payload.kind)
  if (!resolver) {
    return {
      kind: 'FAILED',
      message: `Line does not yet know how to complete "${payload.kind}".`,
      recoverable: false,
    }
  }
  try {
    return await resolver(payload, context)
  } catch (error) {
    return {
      kind: 'FAILED',
      message: error instanceof Error ? error.message : 'The confirmation step failed.',
      recoverable: true,
    }
  }
}

export const __resetLineExecuteConfirmResolversForTests = (): void => {
  RESOLVERS.clear()
}
